/**
 * Profile (INVENTORY §1 screen 10, §4.8; prototype template lines 696-776).
 * Reached from the header avatar; no props, no local state.
 *
 * The identity and password fields live in the ui slice (`pfName`, `pfEmail`,
 * `pfCur`, `pfN1`, `pfN2`) and are pre-filled by `nav('profile')`, so the 84px
 * avatar caption tracks what you are typing — same binding the prototype used.
 *
 * The one piece of real work this screen does is the avatar crop: `onPickAv()`
 * takes a **data URL** (PORTING-NOTES P2-04), so FileReader → Image →
 * centre-crop → `toDataURL('image/jpeg', 0.85)` happens here and the store just
 * stores the string.
 *
 * DEVIATIONS
 *  - D-10: the crop canvas is 256×256, not the prototype's 128×128 — the
 *    avatar renders at 84 CSS px and 128 is soft on retina. The quality (0.85)
 *    and the centre-crop geometry are unchanged.
 *  - D-01: password copy comes from the store (`PASSWORD_MIN_COPY_PROFILE`,
 *    8 characters). The PASSWORD row's `8+` is derived from `PASSWORD_MIN` in
 *    `profileVM`, never written out here — the fields themselves moved into
 *    `sheets/PasswordSheet.tsx` (DESIGN-CHANGELOG 2026-08-18).
 *  - The Steam panel's call-to-action background is `var(--accSoft)` rather
 *    than the template's literal `rgba(90,240,150,.06)`, which hardcoded the
 *    green accent and would not have followed the theme switcher (§10).
 */

import type { CSSProperties } from 'react';

import { CharacterPanel } from '../components/CharacterPanel.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { useQd } from '../store/index.ts';
import { pfSteamDot, PF_STEAM_CAP } from '../store/selectors.3b.ts';
import { profileVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '12px',
  padding: '12px',
  outline: 'none',
};

const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '9px',
  color: 'var(--gt-muted-2)',
};

const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };

const PANEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--gt-border)',
  background: 'var(--gt-panel)',
  boxShadow: 'var(--gt-shadow-card)',
};

const PANEL_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '10px',
  background: 'var(--gt-raised)',
  borderBottom: '1px solid var(--gt-border)',
  padding: '12px 14px',
};

const PANEL_BODY: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '13px',
  padding: '14px',
};

/** D-10 — retina-safe square. The prototype cropped to 128. */
const AVATAR_PX = 256;

/**
 * Prototype `onPickAv` (line 1911), one deviation: 256 px. Centre-crops the
 * largest square the image allows, so a portrait or landscape source never
 * distorts, and hands the store a data URL.
 */
function cropToDataUrl(file: File, done: (dataUrl: string) => void): void {
  const rd = new FileReader();
  rd.onload = () => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = AVATAR_PX;
      c.height = AVATAR_PX;
      const x = c.getContext('2d');
      if (!x) return;
      const sz = Math.min(img.width, img.height);
      x.drawImage(img, (img.width - sz) / 2, (img.height - sz) / 2, sz, sz, 0, 0, AVATAR_PX, AVATAR_PX);
      done(c.toDataURL('image/jpeg', 0.85));
    };
    img.src = String(rd.result);
  };
  rd.readAsDataURL(file);
}

export default function Profile(): JSX.Element {
  const s = useQd();
  // `now` defaults to Date.now(); the ui slice's `tick` re-renders this screen
  // every 5 s (prototype `componentDidMount`), which re-reads the clock.
  const v = profileVM(s);

  return (
    <div
      data-screen-label="Profile"
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 14px 26px' }}
    >
      {/* PROFILE is MY GAMES' sub-page (DESIGN-CHANGELOG 2026-09-03): the way
          back is this 44px control, `design/GT Mobile Crew.dc.html:431`. */}
      <div>
        <span
          onClick={() => s.nav('mine')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '44px',
            padding: '0 12px',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text-4)',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9.5px',
            letterSpacing: '.05em',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          ◂ MY GAMES
        </span>
      </div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <label
          style={{
            position: 'relative',
            width: '84px',
            height: '84px',
            background: v.pfHueBg,
            border: '1px solid var(--gt-ink)',
            /* hard offset shadow, never blurred — the frozen design's own
               literal (template line 698); no token carries .55 */
            boxShadow: '5px 5px 0 rgba(0,0,0,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '22px',
            color: v.pfInk,
            overflow: 'hidden',
            flex: 'none',
            cursor: 'pointer',
          }}
        >
          <span>{v.pfIni}</span>
          {v.pfHasAv ? <div style={{ position: 'absolute', inset: 0, background: v.pfAvBg }} /> : null}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(5,5,8,.78)',
              padding: '5px 0',
              textAlign: 'center',
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: '5px',
              color: 'var(--gt-text)',
            }}
          >
            TAP TO UPLOAD
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = '';
              if (f) cropToDataUrl(f, (url) => void s.onPickAv(url));
            }}
            style={{ display: 'none' }}
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '12px', color: 'var(--gt-text)' }}>
            <span>{v.pfName}</span>
          </div>
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9px',
              letterSpacing: '.04em',
              lineHeight: 1.7,
              color: 'var(--gt-muted)',
            }}
          >
            <span>{v.myStatsTxt}</span>
          </div>
        </div>
      </div>

      {/* The `◈ PARTY · N PLAYERS · ROSTER + ADMIN ▸` door that sat here is gone
          (DESIGN-CHANGELOG 2026-09-03 "PARTY is a phone tab"): the tab bar
          carries PARTY now, the way the desktop nav already did. */}

      <CharacterPanel />

      <div style={PANEL}>
        <div style={PANEL_HEAD}>
          <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '8px', color: 'var(--gt-text)' }}>IDENTITY</div>
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '8.5px',
              letterSpacing: '.05em',
              color: 'var(--gt-muted-2)',
            }}
          >
            HOW YOUR PARTY SEES YOU
          </div>
        </div>
        <div style={PANEL_BODY}>
          <div style={GROUP}>
            <span style={LABEL}>DISPLAY NAME</span>
            <input
              value={v.pfName}
              onChange={(e) => s.setProfileForm({ pfName: e.target.value })}
              style={FIELD}
            />
          </div>
          <PixelButton label="SAVE CHANGES" onClick={() => void s.savePf()} fs={8} pad="14px 14px" center />
        </div>
      </div>

      {/* CHANGE EMAIL — the login address (docs/ACCOUNT-IDENTITY.md).
          DESIGNED, NOT PORTED, and worth knowing why. Neither mirror specifies
          an email-change control at all: the design's only email field is the
          IDENTITY panel's `RECOVERY EMAIL`, saved by SAVE CHANGES with no
          password gate — while the prototype's own login matches against that
          same value. So the design treats one field as both "recovery note" and
          "login identity", and its copy says the first while its logic does the
          second.
          Marcus settled it (2026-08-19): the email IS the login, and changing
          it requires the current password. That cannot live behind SAVE
          CHANGES, so it borrows the shape the phone column already uses for the
          other thing that needs a password — the 52px row plus a sheet. The
          RECOVERY EMAIL field is gone from IDENTITY because it was editing a
          column that is being retired, and since the last commit was not
          saving anything at all. */}
      <div
        onClick={s.openEm}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: '1px solid var(--gt-border)',
          background: 'var(--gt-card)',
          padding: '0 14px',
          height: '52px',
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ fontSize: '13px', color: 'var(--gt-text-5)', lineHeight: 1 }}>▮</span>
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '7.5px',
            color: 'var(--gt-text)',
            transform: 'translateY(0.13em)',
            flex: 'none',
          }}
        >
          LOGIN EMAIL
        </span>
        <span
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9.5px',
            color: 'var(--gt-text-5)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{v.pfAuthEmail}</span>
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '12px', color: 'var(--gt-text-5)' }}>▸</span>
      </div>

      {/* The SECURITY panel's three fields moved into `sheets/PasswordSheet.tsx`
          behind this row — same shape as the FRIENDS row above it. On a temp
          password the row itself warns: border and copy go `--acc2`.
          `design/GT Mobile Crew.dc.html` (the `openPw` row), DESIGN-CHANGELOG
          2026-08-18. Desktop keeps its inline panel. */}
      <div
        onClick={s.openPw}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: '1px solid ' + v.pwRowBd,
          background: 'var(--gt-card)',
          padding: '0 14px',
          height: '52px',
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ fontSize: '13px', color: v.pwRowClr, lineHeight: 1 }}>▮</span>
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '7.5px',
            color: 'var(--gt-text)',
            transform: 'translateY(0.13em)',
            flex: 'none',
          }}
        >
          PASSWORD
        </span>
        <span
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9.5px',
            color: v.pwRowClr,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{v.pwRowTxt}</span>
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '12px', color: 'var(--gt-text-5)' }}>▸</span>
      </div>

      <div style={{ ...PANEL, border: '1px solid ' + v.pfSteamBd }}>
        <div style={PANEL_HEAD}>
          <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '8px', color: 'var(--acc)' }}>STEAM ACCOUNT</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '8px', height: '8px', flex: 'none', background: pfSteamDot(s) }} />
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9.5px',
                lineHeight: 1.6,
                color: 'var(--gt-text-3)',
              }}
            >
              {v.pfSteamTxt}
            </span>
          </div>
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '8.5px',
              letterSpacing: '.04em',
              lineHeight: 1.7,
              color: 'var(--gt-muted-2)',
            }}
          >
            {PF_STEAM_CAP}
          </div>
          <div
            onClick={s.openPtSync}
            style={{
              border: '1px solid var(--acc)',
              color: 'var(--acc)',
              background: 'var(--accSoft)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '10.5px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            <span>{v.pfSteamBtn}</span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          border: '1px dashed var(--gt-border-3)',
          padding: '13px 14px',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '9px',
            letterSpacing: '.04em',
            color: 'var(--gt-muted-2)',
          }}
        >
          SESSION — SIGNED IN ON THIS DEVICE
        </div>
        <PixelButton
          label="SIGN OUT ✕"
          onClick={() => void s.signOut()}
          variant="outline"
          tone="acc2"
          fs={8}
          pad="13px 14px"
          center
          shadow={false}
        />
      </div>
    </div>
  );
}
